/**
 * useStreaming — Encapsulates the network-level chat APIs:
 *   • streamChatAPI (SSE)
 *   • callChatAPI (POST /api/chat with text fallback)
 *   • pollExistingAnalysisJob (poll /api/analysis-jobs/:id)
 *
 * No state mutations live here — callers handle UI updates via callbacks.
 */
import { useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';
import { extractErrorFromText } from '@/lib/chatHelpers';
import {
  ANALYSIS_JOB_POLL_INTERVALS,
  ANALYSIS_JOB_MAX_POLLS,
  ANALYSIS_PROGRESS_MESSAGES,
} from '@/lib/chatConstants';

const BACKEND_URL = getBackendBaseUrl();
const API_URL = `${BACKEND_URL}/api`;

export const useStreaming = () => {
  const { session, getAccessToken } = useAuth();

  const callChatAPI = useCallback(async (messages, conversationId, signal, documentPayload = null, requestOptions = {}) => {
    const timeoutMs = Number(requestOptions?.timeoutMs) || 45000;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const body = documentPayload
          ? {
              messages: [],
              conversation_id: conversationId,
              document_text: documentPayload.text,
              question: documentPayload.question,
              language: documentPayload.language,
              user_id: session?.user?.id || null,
              user_email: session?.user?.email || null,
            }
          : {
              messages: messages.map((m) => ({ role: m.role, content: m.content })),
              conversation_id: conversationId,
              user_id: session?.user?.id || null,
              user_email: session?.user?.email || null,
            };

        const response = await axios.post(`${API_URL}/chat`, body, {
          signal,
          timeout: timeoutMs,
          responseType: 'text',
        });

        let payload = response.data;
        if (typeof payload === 'string') {
          const trimmedText = payload.trim();
          if (!trimmedText) throw new Error('Empty chat response');
          try { payload = JSON.parse(trimmedText); }
          catch { throw new Error('Invalid chat response format'); }
        }

        if (payload?.status === 'ok' && typeof payload?.draft_text === 'string') {
          return {
            text: payload.draft_text,
            conversationId: payload.conversation_id || conversationId,
            meta: payload.meta || {
              artifact_type: 'draft_document',
              contract_type: payload.contract_type || 'nda',
              word_count: Number(payload.word_count || 0),
            },
          };
        }

        return {
          text: payload?.text || '',
          conversationId: payload?.conversation_id || conversationId,
          meta: payload?.meta || null,
        };
      } catch (error) {
        if (axios.isCancel(error)) throw new Error('Aborted');
        const errorDetail = error?.response?.data?.detail || error?.response?.data?.error || '';
        if (error?.code === 'ECONNABORTED') throw new Error('REQUEST_TIMEOUT');
        if (attempt === 1) {
          throw new Error(errorDetail || 'Service temporarily unavailable. Please try again.');
        }
      }
    }
  }, [session]);

  const streamChatAPI = useCallback(async (messages, conversationId, signal, onChunk, documentPayload = null, onMeta = null) => {
    const body = documentPayload
      ? {
          messages: [],
          conversation_id: conversationId,
          document_text: documentPayload.text,
          question: documentPayload.question,
          language: documentPayload.language,
          user_id: session?.user?.id || null,
          user_email: session?.user?.email || null,
        }
      : {
          messages: messages.map((m) => ({ role: m.role, content: m.content })),
          conversation_id: conversationId,
          user_id: session?.user?.id || null,
          user_email: session?.user?.email || null,
        };

    const response = await fetch(`${BACKEND_URL}/api/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      let errorText = '';
      try {
        const rawText = await response.text();
        try {
          const errorData = JSON.parse(rawText);
          errorText = errorData?.error || errorData?.detail || '';
        } catch {
          errorText = extractErrorFromText(rawText) || rawText;
        }
      } catch {
        errorText = '';
      }
      throw new Error(errorText || 'Service temporarily unavailable. Please try again.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = '';
    let buffer = '';
    let streamMeta = null;
    let serverConversationId = conversationId || null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6);

        if (data === '[DONE]') {
          const streamError = extractErrorFromText(fullText);
          if (streamError) throw new Error(streamError);
          return { text: fullText, conversationId: serverConversationId || conversationId, meta: streamMeta };
        }

        try {
          const parsed = JSON.parse(data);
          if (parsed.error) throw new Error(parsed.error);
          if (parsed.conversation_id) serverConversationId = parsed.conversation_id;
          if (parsed.meta) {
            streamMeta = parsed.meta;
            if (onMeta) onMeta(parsed.meta);
          }
          if (parsed.text) {
            const chunk = parsed.text;
            if (fullText.length > 0) {
              const lastChar = fullText.slice(-1);
              const prevEndsWithBreak = /[.!?:)،。]\s*$/.test(fullText);
              const newStartsWithBreak = /^[-•*\d]|^[A-Z\u0600-\u06FF]/.test(chunk.trim());
              if (prevEndsWithBreak && newStartsWithBreak && lastChar !== '\n') {
                fullText += '\n\n';
              }
            }
            fullText += chunk;
            onChunk(fullText);
          }
        } catch (e) {
          if (e instanceof SyntaxError) {
            const fallbackError = extractErrorFromText(data);
            if (fallbackError) throw new Error(fallbackError);
            if (data.trim() && data !== '[DONE]') {
              fullText += data;
              onChunk(fullText);
            }
          } else {
            throw e;
          }
        }
      }
    }

    const streamError = extractErrorFromText(fullText);
    if (streamError) throw new Error(streamError);
    return { text: fullText, conversationId: serverConversationId || conversationId, meta: streamMeta };
  }, [session]);

  const pollExistingAnalysisJob = useCallback(async (jobId, conversationId, signal, onProgress, onPartialResult) => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');
    if (!jobId || jobId === 'None' || jobId === 'undefined' || jobId === 'null') {
      throw new Error('Invalid job ID - analysis job was not created properly');
    }

    const sleepWithAbort = (ms) => new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (signal) signal.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      const onAbort = () => {
        clearTimeout(timer);
        if (signal) signal.removeEventListener('abort', onAbort);
        reject(new Error('Aborted'));
      };
      if (signal) {
        if (signal.aborted) { onAbort(); return; }
        signal.addEventListener('abort', onAbort);
      }
    });

    let pollCount = 0;
    let partialResultDelivered = false;

    while (pollCount < ANALYSIS_JOB_MAX_POLLS) {
      if (signal?.aborted) throw new Error('Aborted');
      const pollInterval = ANALYSIS_JOB_POLL_INTERVALS[Math.min(pollCount, ANALYSIS_JOB_POLL_INTERVALS.length - 1)];
      await sleepWithAbort(pollInterval);

      const statusResponse = await axios.get(`${API_URL}/analysis-jobs/${jobId}`, {
        signal,
        timeout: 15000,
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = statusResponse?.data || {};
      const status = payload?.status;

      if (onProgress && payload?.stage) {
        const stageMessages = ANALYSIS_PROGRESS_MESSAGES[payload.stage] || ANALYSIS_PROGRESS_MESSAGES.pending;
        const lang = payload?.language || 'ar';
        const message = stageMessages[lang] || stageMessages.ar;
        onProgress(message, payload.stage);
      }

      if (!partialResultDelivered && payload?.partial_result && onPartialResult) {
        partialResultDelivered = true;
        onPartialResult({
          partialResult: payload.partial_result,
          status: payload.status,
          stage: payload.stage,
        });
      }

      if (status === 'completed' || status === 'partial_success') {
        const finalResult = payload?.final_result || {};
        const fullReportSections = finalResult?.full_report_sections || {};
        return {
          type: 'analysis_job_completed',
          text: finalResult?.review_text || 'Analysis completed.',
          conversationId,
          meta: {
            type: 'analysis_job_completed',
            review_scorecard: finalResult?.review_scorecard || payload?.review_scorecard,
            doc_type: finalResult?.doc_type || payload?.doc_type,
            doc_type_label: finalResult?.doc_type_label || payload?.doc_type_label,
            party_label: finalResult?.party_label || payload?.party_label,
            sources: finalResult?.sources || [],
            has_structured_report: finalResult?.has_structured_report || false,
            partial_success: finalResult?.partial_success || false,
            error_flags: finalResult?.error_flags,
            analysis_job: payload,
            executive_decision: payload?.executive_decision,
            risk_score: payload?.executive_decision?.risk_score || payload?.risk_score,
            critical_risks: payload?.executive_decision?.critical_risks || payload?.critical_risks,
            must_fix_clauses: payload?.executive_decision?.must_fix_clause_numbers || payload?.must_fix_clauses,
            negotiation_priorities: payload?.executive_decision?.negotiation_priorities,
            final_report: payload?.final_report,
            redlines: payload?.final_report?.redlines || payload?.redlines,
            structural_issues: payload?.final_report?.structural_issues || payload?.structural_issues,
            missing_protections: payload?.final_report?.missing_protections || payload?.missing_protections,
            top_actions: payload?.final_report?.top_actions || payload?.top_actions,
            enhanced_clause_reviews: payload?.final_report?.enhanced_clause_reviews || payload?.enhanced_clause_reviews,
            full_report_sections: fullReportSections,
            has_full_report: finalResult?.has_structured_report || Object.keys(fullReportSections).length > 0,
          },
        };
      }

      if (status === 'failed' || status === 'failed_partial') {
        throw new Error(payload?.error_message || 'Analysis job failed');
      }

      pollCount++;
    }

    throw new Error('REQUEST_TIMEOUT');
  }, [getAccessToken]);

  return { callChatAPI, streamChatAPI, pollExistingAnalysisJob };
};
